import React, { useState } from "react";
import {
  ArrowUp,
  ArrowDown,
  Reply,
  User,
  Clock,
  Edit3,
  Trash2,
  MoreHorizontal,
  ChevronDown,
  ChevronUp,
  MessageSquare,
} from "lucide-react";
import { toast } from "react-toastify";
import communityService from "../../../services/communityService";
import "./ReplyComponent.css";

const ReplyComponent = ({
  reply,
  onVote,
  onReply,
  formatTimeAgo,
  expandedReplies,
  onToggleExpansion,
  isModeratorOrCreator,
  communityId,
  threadId,
  depth = 0,
}) => {
  const [showActions, setShowActions] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(reply.content);
  const [submittingEdit, setSubmittingEdit] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const maxDepth = 5; // Maximum nesting depth
  const isExpanded = expandedReplies.has(reply._id);
  const hasChildren = reply.children && reply.children.length > 0;
  const canReply = depth < maxDepth;

  const handleVoteClick = (e) => {
    e.stopPropagation();
    onVote(reply._id);
  };

  const handleReplyClick = (e) => {
    e.stopPropagation();
    onReply(reply);
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    setEditing(true);
    setEditText(reply.content);
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setEditText(reply.content);
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();

    if (!editText.trim()) {
      toast.error("Reply cannot be empty");
      return;
    }

    if (editText.trim() === reply.content) {
      setEditing(false);
      return;
    }

    try {
      setSubmittingEdit(true);

      const response = await communityService.updateReply(
        communityId,
        threadId,
        reply._id,
        editText.trim()
      );

      if (response.success) {
        // Update reply content locally (parent component should handle this ideally)
        reply.content = editText.trim();
        setEditing(false);
        toast.success("Reply updated successfully!");
      } else {
        throw new Error("Failed to update reply");
      }
    } catch (error) {
if (error.status === 403) {
        if (error.message.includes("15 minutes")) {
          toast.error("Reply can only be edited within 15 minutes of creation");
        } else {
          toast.error("Only the reply author can edit this reply");
        }
      } else {
        toast.error(error.message || "Failed to update reply");
      }
    } finally {
      setSubmittingEdit(false);
    }
  };

  const handleDeleteClick = async (e) => {
    e.stopPropagation();

    if (!window.confirm("Are you sure you want to delete this reply?")) {
      return;
    }

    try {
      setDeleting(true);

      const response = await communityService.deleteReply(
        communityId,
        threadId,
        reply._id
      );

      if (response.success) {
        toast.success("Reply deleted successfully!");
        // Parent component should handle removing from list
        // For now, just mark as deleted locally
        reply.content = "[deleted]";
        reply.isDeleted = true;
      } else {
        throw new Error("Failed to delete reply");
      }
    } catch (error) {
if (error.status === 403) {
        toast.error("Only the reply author or moderators can delete replies");
      } else {
        toast.error(error.message || "Failed to delete reply");
      }
    } finally {
      setDeleting(false);
    }
  };

  const getAuthorProfileImage = () => {
    return reply.author?.signedProfilePhotoUrl || reply.author?.profilePhotoUrl;
  };

  const canEditReply = () => {
    // Check if current user is the author and within 15 minutes
    const now = new Date();
    const created = new Date(reply.createdAt);
    const minutesSinceCreation = (now - created) / (1000 * 60);

    // This check should ideally come from backend, but for UI purposes
    return minutesSinceCreation <= 15;
  };

  const canDeleteReply = () => {
    // Author can delete, or moderators can delete
    return true; // Backend will handle the actual permission check
  };

  if (reply.isDeleted) {
    return (
      <div className={`reply deleted depth-${Math.min(depth, 5)}`}>
        <div className="reply-content">
          <span className="deleted-message">[This reply has been deleted]</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`reply depth-${Math.min(depth, 5)}`}>
      {/* Vote Section */}
      <div className="reply-vote-section">
        <button
          className={`vote-btn upvote ${reply.userHasUpvoted ? "active" : ""}`}
          onClick={handleVoteClick}
          title="Upvote"
        >
          <ArrowUp size={14} />
        </button>
        <span className="vote-count">{reply.upvoteCount || 0}</span>
      </div>

      {/* Content Section */}
      <div className="reply-content">
        {/* Header */}
        <div className="reply-header">
          <div className="reply-author">
            <div className="author-avatar">
              {getAuthorProfileImage() ? (
                <img
                  src={getAuthorProfileImage()}
                  alt={reply.author.name}
                  className="author-image"
                />
              ) : (
                <User size={12} />
              )}
            </div>
            <span className="author-name">{reply.author.name}</span>
          </div>

          <span className="reply-timestamp">
            <Clock size={10} />
            {formatTimeAgo(reply.createdAt)}
          </span>

          {/* Action Menu */}
          <div className="reply-actions">
            <button
              className="action-menu-btn"
              onClick={(e) => {
                e.stopPropagation();
                setShowActions(!showActions);
              }}
            >
              <MoreHorizontal size={14} />
            </button>

            {showActions && (
              <div className="action-menu">
                {canReply && (
                  <button onClick={handleReplyClick}>
                    <Reply size={12} />
                    Reply
                  </button>
                )}

                {canEditReply() && (
                  <button onClick={handleEditClick}>
                    <Edit3 size={12} />
                    Edit
                  </button>
                )}

                {(canDeleteReply() || isModeratorOrCreator) && (
                  <button
                    onClick={handleDeleteClick}
                    className="delete-action"
                    disabled={deleting}
                  >
                    <Trash2 size={12} />
                    Delete
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="reply-main">
          {editing ? (
            <form onSubmit={handleSubmitEdit} className="edit-reply-form">
              <textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                maxLength={1500}
                rows={3}
                autoFocus
                disabled={submittingEdit}
              />

              <div className="edit-form-actions">
                <div className="char-count">{editText.length}/1500</div>

                <div className="action-buttons">
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="cancel-btn"
                    disabled={submittingEdit}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="save-btn"
                    disabled={!editText.trim() || submittingEdit}
                  >
                    {submittingEdit ? (
                      <>
                        <div className="loading-spinner-small"></div>
                        Saving...
                      </>
                    ) : (
                      "Save"
                    )}
                  </button>
                </div>
              </div>
            </form>
          ) : (
            <p className="reply-text">{reply.content}</p>
          )}
        </div>

        {/* Footer */}
        <div className="reply-footer">
          <div className="reply-stats">
            {reply.upvoteCount > 0 && (
              <span className="stat">
                <ArrowUp size={10} />
                {reply.upvoteCount}
              </span>
            )}

            {hasChildren && (
              <span className="stat">
                <MessageSquare size={10} />
                {reply.replyCount}{" "}
                {reply.replyCount === 1 ? "reply" : "replies"}
              </span>
            )}
          </div>

          <div className="reply-actions-inline">
            {canReply && !editing && (
              <button className="inline-action-btn" onClick={handleReplyClick}>
                <Reply size={12} />
                Reply
              </button>
            )}
          </div>
        </div>

        {/* Nested Replies */}
        {hasChildren && (
          <div className="nested-replies">
            {/* Toggle Button */}
            <button
              className="toggle-replies-btn"
              onClick={() => onToggleExpansion(reply._id)}
            >
              {isExpanded ? (
                <>
                  <ChevronUp size={14} />
                  Hide {reply.children.length}{" "}
                  {reply.children.length === 1 ? "reply" : "replies"}
                </>
              ) : (
                <>
                  <ChevronDown size={14} />
                  Show {reply.children.length}{" "}
                  {reply.children.length === 1 ? "reply" : "replies"}
                </>
              )}
            </button>

            {/* Nested Replies List */}
            {isExpanded && (
              <div className="nested-replies-list">
                {reply.children.map((childReply) => (
                  <ReplyComponent
                    key={childReply._id}
                    reply={childReply}
                    onVote={onVote}
                    onReply={onReply}
                    formatTimeAgo={formatTimeAgo}
                    expandedReplies={expandedReplies}
                    onToggleExpansion={onToggleExpansion}
                    isModeratorOrCreator={isModeratorOrCreator}
                    communityId={communityId}
                    threadId={threadId}
                    depth={depth + 1}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Memoize ReplyComponent to prevent unnecessary re-renders in nested structures
export default React.memo(ReplyComponent, (prevProps, nextProps) => {
  return (
    prevProps.reply._id === nextProps.reply._id &&
    prevProps.reply.content === nextProps.reply.content &&
    prevProps.reply.upvoteCount === nextProps.reply.upvoteCount &&
    prevProps.reply.userHasUpvoted === nextProps.reply.userHasUpvoted &&
    prevProps.reply.replyCount === nextProps.reply.replyCount &&
    prevProps.expandedReplies === nextProps.expandedReplies &&
    prevProps.isModeratorOrCreator === nextProps.isModeratorOrCreator &&
    prevProps.depth === nextProps.depth
  );
});
